import { WidgetProps } from '@rjsf/utils';
import { useMemo } from 'react';
import { Divider } from '@mui/material';
import { AddCircle, Edit } from '@mui/icons-material';
import { PillIconButton } from 'components/button/PillIconButton';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import useDialog from '../../hooks/useDialog.tsx';
import ViewDialog from '../dialog/ViewDialog.tsx';
import usePythonFunctions from '../../api/python-functions/usePythonFunctions.ts';
import Loader from '../Loader.tsx';
import ListSelectAutocomplete from './ListSelectAutocomplete.tsx';
import PythonFunctionRenderer from './CustomRenderers/PythonFunctionRenderer.tsx';
import PythonFunctionAdd from '../../views/python-functions/PythonFunctionAdd.tsx';
import PythonFunctionEdit from '../../views/python-functions/PythonFunctionEdit.tsx';
import { FormBlockContext } from './FormBlock.tsx';

const PythonFunctionWidget = (props: WidgetProps) => {
  const { projectId = '', workflowId = '' } = useParams();
  const [showAddDialog, AddDialog] = useDialog(ViewDialog);
  const [showEditDialog, EditDialog] = useDialog(ViewDialog);
  const [, setSearchParams] = useSearchParams();

  const inputCount = (props.formContext as FormBlockContext | undefined)?.pythonFunctionInputCount;
  const { functions, isFetching } = usePythonFunctions(projectId, true);

  const functionsById = useMemo(() => new Map((functions ?? []).map((fn) => [fn.id, fn])), [functions]);

  const functionOptions = useMemo(
    () =>
      functions?.map((fn) => ({
        value: fn.name,
        const: fn.id,
        group: fn.folder_path?.trim() || undefined,
      })) ?? [],
    [functions],
  );

  const handleCreated = (functionId: string) => {
    props.onChange(functionId);
    showAddDialog(false);
  };

  const handleEdited = (functionId: string) => {
    props.onChange(functionId);
    showEditDialog(false);
  };

  const handleCloseEditDialog = () => {
    setSearchParams({});
  };

  return (
    <>
      <div>Python function settings</div>
      <Divider className="mb-6" />
      <div className="flex w-100 flex-row items-center gap-2">
        {!isFetching ? (
          <ListSelectAutocomplete
            options={functionOptions}
            value={props.value}
            onChange={props.onChange}
            label={props.schema.title}
            required={props.required}
            disabled={props.disabled || props.readonly || isFetching}
            render={(functionId) => (
              <PythonFunctionRenderer pythonFunction={functionsById.get(functionId)} fallback={functionId} />
            )}
          />
        ) : (
          <Loader />
        )}

        <PillIconButton
          title="Edit function"
          onClick={() => showEditDialog(true)}
          disabled={!props.value || isFetching || props.disabled}
          color="info"
        >
          <Edit />
        </PillIconButton>
        <PillIconButton
          color="info"
          title="Add function"
          disabled={isFetching || props.disabled}
          onClick={() => showAddDialog(true)}
        >
          <AddCircle />
        </PillIconButton>
      </div>
      <AddDialog>
        <PythonFunctionAdd
          projectId={projectId ?? ''}
          inputCount={inputCount}
          onSubmit={handleCreated}
          onCancel={() => showAddDialog(false)}
        />
      </AddDialog>
      <EditDialog onCancel={handleCloseEditDialog}>
        <PythonFunctionEdit
          onSubmit={handleEdited}
          functionId={props.value}
          projectId={projectId}
          activeWorkflowId={workflowId}
          inputCount={inputCount}
          onCancel={() => showEditDialog(false)}
        />
      </EditDialog>
    </>
  );
};

export default PythonFunctionWidget;
